import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  Popover,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineRoundedIcon from '@material-ui/icons/ErrorOutlineRounded';
import ListRowView from '../../components/ListRow/ListRow-view';

const FactSheetCell = ({
  t,
  ticker,
  factSheetFiles,
  attachedFile,
  onAttach,
  onDeactivate,
}) => {
  const existing = factSheetFiles.find((f) => f.ticker === ticker);
  const [confirmAnchor, setConfirmAnchor] = useState(null);

  return (
    <Box>
      <Typography variant="body2" noWrap>
        {attachedFile ? attachedFile.name : existing?.factSheetName || '-'}
      </Typography>
      <Button component="label" size="small">
        {t('AttachFile')}
        <input
          type="file"
          accept="application/pdf"
          hidden
          onChange={(e) => onAttach(ticker, e.target.files[0] || null)}
        />
      </Button>
      {existing && !attachedFile && (
        <>
          <Button
            size="small"
            color="secondary"
            onClick={(e) => setConfirmAnchor(e.currentTarget)}
          >
            {t('DeleteFile')}
          </Button>
          <Popover
            open={Boolean(confirmAnchor)}
            anchorEl={confirmAnchor}
            onClose={() => setConfirmAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Box p={2} maxWidth={220}>
              <Typography variant="body2" gutterBottom>
                {t('DeleteFileConfirm')}
              </Typography>
              <Box display="flex" justifyContent="flex-end">
                <Button size="small" onClick={() => setConfirmAnchor(null)}>
                  {t('Cancel')}
                </Button>
                <Button
                  size="small"
                  color="secondary"
                  onClick={() => {
                    onDeactivate(existing.factSheetFileId);
                    setConfirmAnchor(null);
                  }}
                >
                  {t('DeleteFile')}
                </Button>
              </Box>
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

FactSheetCell.propTypes = {
  t: PropTypes.func.isRequired,
  ticker: PropTypes.string.isRequired,
  factSheetFiles: PropTypes.array.isRequired,
  attachedFile: PropTypes.object,
  onAttach: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired,
};

FactSheetCell.defaultProps = {
  attachedFile: null,
};

const Review = ({
  t,
  parsedFunds,
  assetClasses,
  openPalms,

  // Fund Objects to Submit
  fluFuncs,
  conflictFunds,
  addFunds,
  removeFunds,
  createFunds,
  confirm,
  confirmStatus,

  factSheetFiles,
  factSheetAttachments,
  setFactSheetAttachment,
  deactivateFactSheetFile,
}) => (
  <>
    {confirmStatus === 'init' && (
      <>
        <Box p={2}>
          {parsedFunds && addFunds && (
            <Box mb={2}>
              <Typography variant="h6">{`${t('Additions')}: ${
                Object.keys(parsedFunds.add).length
              }`}</Typography>
              <Box
                pl={7}
                pr={3}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                {t('ListHeadings', { returnObjects: true }).map((c) => (
                  <Box minWidth="120pt" flex="1" key={c}>
                    <Typography variant="caption" color="textSecondary">
                      {c}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {Object.keys(parsedFunds.add).length === 0 && (
                <Typography>{t('None')}</Typography>
              )}
              {Object.values(parsedFunds.add).map((x) => {
                if (x.length > 0) {
                  const fundKey = x[0].ticker;
                  const fundData = addFunds[fundKey];
                  if (!fundData) return null;
                  return (
                    <ListRowView
                      key={fundKey}
                      checkbox
                      selected={fundData.confirmed || false}
                      onCheck={() => {
                        fluFuncs.setAddFunds({
                          ...addFunds,
                          [fundKey]: {
                            ...fundData,
                            confirmed: !fundData.confirmed,
                          },
                        });
                      }}
                      paletteColor="secondary"
                      hasNodes
                      nodes={[
                        t('Ok'),
                        x[0].ticker,
                        x[0].fundName,
                        x[0].cusip,
                        assetClasses[x[0].assetClassId - 1],
                        <FactSheetCell
                          t={t}
                          ticker={x[0].ticker}
                          factSheetFiles={factSheetFiles}
                          attachedFile={factSheetAttachments[x[0].ticker]}
                          onAttach={setFactSheetAttachment}
                          onDeactivate={deactivateFactSheetFile}
                        />,
                      ]}
                    />
                  );
                }
                return null;
              })}
            </Box>
          )}
          {parsedFunds && conflictFunds && (
            <Box mb={2}>
              <Typography variant="h6">{`${t('Conflicts')}: ${
                Object.keys(parsedFunds.conflict).length
              }`}</Typography>
              <Box
                pl={7}
                pr={3}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                {t('ListHeadings', { returnObjects: true }).map((c) => (
                  <Box minWidth="120pt" flex="1" key={c}>
                    <Typography variant="caption" color="textSecondary">
                      {c}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {Object.keys(parsedFunds.conflict).length === 0 && (
                <Typography>{t('None')}</Typography>
              )}
              {Object.values(parsedFunds.conflict).map((x) => {
                if (x.length > 0) {
                  const fundKey = x[0].ticker;
                  const fundData = conflictFunds[fundKey];
                  if (!fundData) return null;
                  return (
                    <ListRowView
                      key={fundKey}
                      checkbox
                      selected={fundData.confirmed}
                      onCheck={() => {
                        fluFuncs.setConflictFunds({
                          ...conflictFunds,
                          [fundKey]: {
                            ...fundData,
                            confirmed: !fundData.confirmed,
                          },
                        });
                      }}
                      paletteColor="warning"
                      hasNodes
                      nodes={[
                        t('ConfAttn'),
                        x[0].ticker,
                        <Select
                          onChange={(e) => {
                            fluFuncs.setConflictFunds({
                              ...conflictFunds,
                              [fundKey]: {
                                ...fundData,
                                fundId: e.target.value,
                              },
                            });
                          }}
                          fullWidth
                        >
                          {x.map((f) => (
                            <MenuItem key={f.fundId} value={f.fundId}>
                              {f.fundName}
                            </MenuItem>
                          ))}
                        </Select>,
                        x[0].cusip,
                        assetClasses[x[0].assetClassId - 1],
                        <FactSheetCell
                          t={t}
                          ticker={x[0].ticker}
                          factSheetFiles={factSheetFiles}
                          attachedFile={factSheetAttachments[x[0].ticker]}
                          onAttach={setFactSheetAttachment}
                          onDeactivate={deactivateFactSheetFile}
                        />,
                      ]}
                    />
                  );
                }
                return null;
              })}
            </Box>
          )}
          {parsedFunds && (
            <Box mb={2}>
              <Typography variant="h6">{`${t('Unchanged')}: ${
                Object.keys(parsedFunds.unchanged).length
              }`}</Typography>
              <Box
                pl={7}
                pr={3}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                {t('ListHeadings', { returnObjects: true }).map((c) => (
                  <Box minWidth="120pt" flex="1" key={c}>
                    <Typography variant="caption" color="textSecondary">
                      {c}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {Object.keys(parsedFunds.unchanged).length === 0 && (
                <Typography>{t('None')}</Typography>
              )}
              {Object.values(parsedFunds.unchanged).map((x) => {
                if (x.length > 0) {
                  return (
                    <ListRowView
                      key={x[0].ticker}
                      paletteColor="grey"
                      hasNodes
                      nodes={[
                        t('Ok'),
                        x[0].ticker,
                        x[0].fundName,
                        x[0].cusip,
                        assetClasses[x[0].assetClassId - 1],
                        <FactSheetCell
                          t={t}
                          ticker={x[0].ticker}
                          factSheetFiles={factSheetFiles}
                          attachedFile={factSheetAttachments[x[0].ticker]}
                          onAttach={setFactSheetAttachment}
                          onDeactivate={deactivateFactSheetFile}
                        />,
                      ]}
                    />
                  );
                }
                return null;
              })}
            </Box>
          )}
          {parsedFunds && removeFunds && (
            <Box mb={2}>
              <Typography variant="h6">{`${t('Removals')}: ${
                Object.keys(parsedFunds.remove).length
              }`}</Typography>
              <Box
                pl={7}
                pr={3}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                {t('ListHeadings', { returnObjects: true }).map((c) => (
                  <Box minWidth="120pt" flex="1" key={c}>
                    <Typography variant="caption" color="textSecondary">
                      {c}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {Object.keys(parsedFunds.remove).length === 0 && (
                <Typography>{t('None')}</Typography>
              )}
              {Object.values(parsedFunds.remove).map((x) => {
                if (x.length > 0) {
                  const fundKey = x[0].ticker;
                  const fundData = removeFunds[fundKey];
                  if (!fundData) return null;
                  return (
                    <ListRowView
                      key={fundKey}
                      checkbox
                      selected={fundData.confirmed}
                      onCheck={() => {
                        fluFuncs.setRemoveFunds({
                          ...removeFunds,
                          [fundKey]: {
                            ...fundData,
                            confirmed: !fundData.confirmed,
                          },
                        });
                      }}
                      paletteColor="error"
                      hasNodes
                      nodes={[
                        t('Ok'),
                        x[0].ticker,
                        x[0].fundName,
                        x[0].cusip,
                        assetClasses[x[0].assetClassId - 1],
                        <FactSheetCell
                          t={t}
                          ticker={x[0].ticker}
                          factSheetFiles={factSheetFiles}
                          attachedFile={factSheetAttachments[x[0].ticker]}
                          onAttach={setFactSheetAttachment}
                          onDeactivate={deactivateFactSheetFile}
                        />,
                      ]}
                    />
                  );
                }
                return null;
              })}
            </Box>
          )}
          {parsedFunds && createFunds && (
            <Box mb={2}>
              <Typography variant="h6">{`${t('Created')}: ${
                Object.keys(parsedFunds.create).length
              }`}</Typography>
              <Box
                pl={7}
                pr={3}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                {t('ListHeadings', { returnObjects: true }).map((c) => (
                  <Box minWidth="120pt" flex="1" key={c}>
                    <Typography variant="caption" color="textSecondary">
                      {c}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {Object.keys(parsedFunds.create).length === 0 && (
                <Typography>{t('None')}</Typography>
              )}
              {Object.keys(parsedFunds.create).map((x) => {
                const fundData = createFunds[x];
                if (!fundData) return null;
                return (
                  <ListRowView
                    key={x}
                    checkbox
                    selected={fundData.confirmed}
                    onCheck={() => {
                      fluFuncs.setCreateFunds({
                        ...createFunds,
                        [x]: {
                          ...fundData,
                          confirmed: !fundData.confirmed,
                        },
                      });
                    }}
                    paletteColor="primary"
                    hasNodes
                    nodes={[
                      <Typography>{t('NewAttn')}</Typography>,
                      <Typography>{x}</Typography>,
                      <TextField
                        onChange={(e) => {
                          fluFuncs.setCreateFunds({
                            ...createFunds,
                            [x]: {
                              ...fundData,
                              fundName: e.target.value,
                            },
                          });
                        }}
                        label={t('FundName')}
                        value={fundData.fundName}
                      />,
                      <TextField
                        onChange={(e) => {
                          fluFuncs.setCreateFunds({
                            ...createFunds,
                            [x]: {
                              ...fundData,
                              cusip: e.target.value,
                            },
                          });
                        }}
                        label={t('CUSIP')}
                      />,
                      <TextField
                        onChange={(e) => {
                          fluFuncs.setCreateFunds({
                            ...createFunds,
                            [x]: {
                              ...fundData,
                              assetClassId: e.target.value,
                            },
                          });
                        }}
                        select
                        label={t('AssetClass')}
                        value={fundData.assetClassId || ''}
                      >
                        {assetClasses.map((c, i) => (
                          <MenuItem key={c} value={i + 1}>
                            {c}
                          </MenuItem>
                        ))}
                      </TextField>,
                      <FactSheetCell
                        t={t}
                        ticker={x}
                        factSheetFiles={factSheetFiles}
                        attachedFile={factSheetAttachments[x]}
                        onAttach={setFactSheetAttachment}
                        onDeactivate={deactivateFactSheetFile}
                      />,
                    ]}
                  />
                );
              })}
            </Box>
          )}
        </Box>
        {addFunds &&
          removeFunds &&
          conflictFunds &&
          parsedFunds &&
          createFunds && (
            <Box m={2} p={2} border="1pt solid">
              <Typography>
                {`${t('FundstoAdd')}: ${
                  Object.values(addFunds).filter((x) => x && x.confirmed)
                    .length +
                  Object.values(conflictFunds).filter((x) => x && x.confirmed)
                    .length
                }`}
              </Typography>
              <Typography>
                {`${t('FundstoRemove')}: ${
                  Object.values(removeFunds).filter((x) => x && x.confirmed)
                    .length
                }`}
              </Typography>
              <Typography>
                {`${t('FundstoCreate')}: ${
                  Object.values(createFunds).filter((x) => x && x.confirmed)
                    .length
                }`}
              </Typography>
              <Typography>
                {`${t('FundsUnchanged')}: ${
                  Object.keys(parsedFunds.unchanged).length
                }`}
              </Typography>
              <Box mt={2}>
                <Button onClick={confirm}>{t('ConfirmAction')}</Button>
              </Box>
            </Box>
          )}
        {!parsedFunds && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mb={3}
          >
            <CircularProgress variant="indeterminate" />
          </Box>
        )}
      </>
    )}
    {confirmStatus === 'loading' && (
      <Box
        display="flex"
        py={3}
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <Typography align="center" variant="h5">
          {t('WaitMessage')}
        </Typography>
        <Box my={2}>
          <CircularProgress variant="indeterminate" />
        </Box>
      </Box>
    )}
    {confirmStatus === 'done' && (
      <Box
        display="flex"
        py={3}
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <Box>
          <Typography align="center" variant="h5" paragraph>
            {t('SuccessMessage')}
          </Typography>
          <Typography align="center">{t('SuccessBody')}</Typography>
        </Box>
        <Box my={2}>
          <CheckCircleIcon color="secondary" fontSize="large" />
        </Box>
        <Button onClick={openPalms}>{t('OpenPalms')}</Button>
      </Box>
    )}
    {confirmStatus === 'err' && (
      <Box
        display="flex"
        py={3}
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <Typography align="center" variant="h5">
          {t('ErrorMessage')}
        </Typography>
        <Typography align="center" variant="body1">
          {t('ErrorAction')}
        </Typography>
        <ErrorOutlineRoundedIcon color="error" fontSize="large" />
      </Box>
    )}
  </>
);

Review.propTypes = {
  t: PropTypes.func.isRequired,
  parsedFunds: PropTypes.object.isRequired,
  assetClasses: PropTypes.arrayOf(PropTypes.string).isRequired,

  fluFuncs: PropTypes.object.isRequired,
  conflictFunds: PropTypes.object.isRequired,
  addFunds: PropTypes.object.isRequired,
  removeFunds: PropTypes.object.isRequired,
  createFunds: PropTypes.object.isRequired,

  confirm: PropTypes.func.isRequired,
  confirmStatus: PropTypes.string.isRequired,

  openPalms: PropTypes.func.isRequired,

  factSheetFiles: PropTypes.array.isRequired,
  factSheetAttachments: PropTypes.object.isRequired,
  setFactSheetAttachment: PropTypes.func.isRequired,
  deactivateFactSheetFile: PropTypes.func.isRequired,
};

export default Review;
